import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AppsIcon from "@mui/icons-material/Apps";
import VisibilityIcon from "@mui/icons-material/Visibility";
import userService, { roleService } from "@/services/userService";
import type { ModuleAccessState, Role, User } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [rolesTarget, setRolesTarget] = useState<User | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const [viewTarget, setViewTarget] = useState<User | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  // Module Access (User Onboarding + Module Access -- Admin control).
  const [moduleAccessTarget, setModuleAccessTarget] = useState<User | null>(null);
  const [moduleAccessState, setModuleAccessState] = useState<ModuleAccessState[]>([]);
  const [moduleAccessLoading, setModuleAccessLoading] = useState(false);
  const [moduleAccessSaving, setModuleAccessSaving] = useState(false);
  const [moduleAccessError, setModuleAccessError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.list({
        query: query || undefined,
        page: page + 1,
        page_size: pageSize,
        sort_by: "created_at",
        sort_order: "desc",
      });
      setUsers(response.items);
      setTotal(response.meta.total);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [query, page, pageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    roleService
      .list()
      .then(setAllRoles)
      .catch((err) => setError(extractErrorMessage(err)));
  }, []);

  const handleToggleActive = async (user: User) => {
    try {
      if (user.is_active) {
        await userService.deactivate(user.id);
      } else {
        await userService.activate(user.id);
      }
      await loadUsers();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await userService.remove(deleteTarget.id);
      setDeleteTarget(null);
      await loadUsers();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    try {
      await userService.resetPassword(resetTarget.id, newPassword);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleOpenRolesDialog = (user: User) => {
    setRolesTarget(user);
    setSelectedRoleIds(user.roles.map((role) => role.id));
  };

  const toggleRoleSelection = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleAssignRoles = async () => {
    if (!rolesTarget) return;
    try {
      await userService.assignRoles(rolesTarget.id, selectedRoleIds);
      setRolesTarget(null);
      await loadUsers();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleViewDetails = async (user: User) => {
    setViewError(null);
    setViewTarget(user); // open immediately with the row's cached data...
    try {
      const fullDetails = await userService.getById(user.id); // ...then refresh from the source of truth
      setViewTarget(fullDetails);
    } catch (err) {
      setViewError(extractErrorMessage(err));
    }
  };

  const handleOpenModuleAccessDialog = async (user: User) => {
    setModuleAccessTarget(user);
    setModuleAccessError(null);
    setModuleAccessLoading(true);
    try {
      const items = await userService.getModuleAccess(user.id);
      setModuleAccessState(items);
    } catch (err) {
      setModuleAccessError(extractErrorMessage(err));
    } finally {
      setModuleAccessLoading(false);
    }
  };

  const toggleModuleAccess = (moduleCode: string) => {
    setModuleAccessState((prev) =>
      prev.map((m) => (m.module_code === moduleCode ? { ...m, enabled: !m.enabled } : m))
    );
  };

  const handleSaveModuleAccess = async () => {
    if (!moduleAccessTarget) return;
    setModuleAccessSaving(true);
    setModuleAccessError(null);
    try {
      const modules = Object.fromEntries(moduleAccessState.map((m) => [m.module_code, m.enabled]));
      await userService.setModuleAccess(moduleAccessTarget.id, modules);
      setModuleAccessTarget(null);
    } catch (err) {
      setModuleAccessError(extractErrorMessage(err));
    } finally {
      setModuleAccessSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        label="Search by name or email"
        value={query}
        onChange={(e) => {
          setPage(0);
          setQuery(e.target.value);
        }}
        size="small"
        sx={{ mb: 2, width: 320 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.roles.map((role) => (
                    <Chip key={role.id} label={role.name} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell align="center">
                  <Switch checked={user.is_active} onChange={() => handleToggleActive(user)} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View details">
                    <IconButton onClick={() => handleViewDetails(user)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Assign roles">
                    <IconButton onClick={() => handleOpenRolesDialog(user)}>
                      <AdminPanelSettingsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Module access">
                    <IconButton onClick={() => handleOpenModuleAccessDialog(user)}>
                      <AppsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset password">
                    <IconButton onClick={() => setResetTarget(user)}>
                      <LockResetIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton color="error" onClick={() => setDeleteTarget(user)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Dialog open={Boolean(resetTarget)} onClose={() => setResetTarget(null)}>
        <DialogTitle>Reset password for {resetTarget?.full_name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={!newPassword}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rolesTarget)} onClose={() => setRolesTarget(null)}>
        <DialogTitle>Assign roles to {rolesTarget?.full_name}</DialogTitle>
        <DialogContent>
          <FormGroup>
            {allRoles.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => toggleRoleSelection(role.id)}
                  />
                }
                label={role.name}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolesTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignRoles}
            disabled={selectedRoleIds.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(moduleAccessTarget)}
        onClose={() => (!moduleAccessSaving ? setModuleAccessTarget(null) : undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Module access for {moduleAccessTarget?.full_name}</DialogTitle>
        <DialogContent>
          {moduleAccessError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {moduleAccessError}
            </Alert>
          )}
          {moduleAccessLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <FormGroup>
              {moduleAccessState.map((m) => (
                <FormControlLabel
                  key={m.module_code}
                  control={<Switch checked={m.enabled} onChange={() => toggleModuleAccess(m.module_code)} />}
                  label={m.display_name}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleAccessTarget(null)} disabled={moduleAccessSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveModuleAccess}
            disabled={moduleAccessLoading || moduleAccessSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewTarget)} onClose={() => setViewTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>User details</DialogTitle>
        <DialogContent>
          {viewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {viewError}
            </Alert>
          )}
          {viewTarget && (
            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
              <Typography variant="body2">
                <strong>Full name:</strong> {viewTarget.full_name}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {viewTarget.email}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {viewTarget.phone_number ?? "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {viewTarget.is_active ? "Active" : "Inactive"}
              </Typography>
              <Typography variant="body2">
                <strong>Verified:</strong> {viewTarget.is_verified ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2">
                <strong>Roles:</strong> {viewTarget.roles.map((r) => r.name).join(", ") || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Last login:</strong>{" "}
                {viewTarget.last_login_at
                  ? new Date(viewTarget.last_login_at).toLocaleString()
                  : "Never"}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {new Date(viewTarget.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete {deleteTarget?.full_name}?</DialogTitle>
        <DialogContent>
          This action permanently deletes the user account and cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagementPage;
